import shuffle from 'lodash/shuffle';

import { useAsyncDataRequestStatus } from '@/composables/utils/useAsyncDataRequestStatus';
import { useSentry } from '@/composables/utils/useSentry';
import type { MinimalEnStoryListViewModel, MinimalEnStoryResponse } from '@/models/wabunka/enStories';
import { useMicroCMSClient } from '@/wabunka-src/common/composables/query/useMicroCMSClient';
import { DEFAULT_PAGE_NUM, MICRO_CMS_FETCH_LIMIT } from '@/wabunka-src/constants/enStories';

type Theme = 'Person' | 'Other';

export interface FilterOptions {
  id?: string;
  theme?: Theme;
}

type FetchOptions =
  | {
      fetchAll: boolean;
    }
  | {
      limit: Ref<number>;
      pageNum?: Ref<number>;
    };

const isFetchAll = (options: FetchOptions): options is { fetchAll: boolean } => {
  return 'fetchAll' in options;
};

export const useEnStories = async (options: FetchOptions, filter?: FilterOptions) => {
  const { captureWabunkaException } = useSentry();
  // ページングのループは useFetch では書けない（await 後に判明する totalCount で回数が決まる）。
  // useRequestFetch を内部で呼ぶため await より前に取得する。
  const { getList } = useMicroCMSClient();

  const offsetParam = computed<number>(() => {
    if (!isFetchAll(options)) {
      return (options.pageNum?.value ?? DEFAULT_PAGE_NUM - DEFAULT_PAGE_NUM) * options.limit.value;
    }
    return 0;
  });

  const limitParam = computed<number>(() => {
    if (!isFetchAll(options)) {
      return options.limit.value;
    }
    return MICRO_CMS_FETCH_LIMIT;
  });

  const filters = computed<string>(() => {
    const base = 'isPrivate[equals]false';

    switch (filter?.theme) {
      case 'Person':
        return `${base},theme[contains]人`;

      case 'Other':
        return `${base},theme[contains]その他`;
    }

    return base;
  });

  // TODO: minimum response型を作成する
  const fields = ['id', 'title', 'thumbnailImage', 'provider'];

  const fetchStories = async (limit: number, offset: number, filters: string) => {
    const contents: MinimalEnStoryListViewModel = [];

    for (let currentOffset = offset; ; ) {
      const response = await getList<MinimalEnStoryResponse>({ endpoint: 'en-stories', queries: { filters, limit, offset: currentOffset, fields } });
      contents.push(...response.contents);

      // 全件取得する場合、一度に取得できる件数が MICRO_CMS_FETCH_LIMIT までなので続きを取る
      const nextOffset = response.offset + limit;
      if (!isFetchAll(options) || nextOffset >= response.totalCount) return contents;
      currentOffset = nextOffset;
    }
  };

  // filter は展開して key に入れる（オブジェクトのまま埋めると常に同じ文字列になり、
  // 条件の違う取得同士が useAsyncData のキャッシュを共有してしまう）
  const cacheKey = `en-stories-${limitParam.value}-${offsetParam.value}-${filter?.id ?? ''}-${filter?.theme ?? ''}-${fields.join('-')}`;

  const { data, status } = await useAsyncData<MinimalEnStoryListViewModel>(
    cacheKey,
    async () => {
      try {
        return await fetchStories(limitParam.value, offsetParam.value, filters.value);
      } catch (e) {
        captureWabunkaException(e as Error);
        throw e;
      }
    },
    { default: () => [] },
  );

  const enStories = computed<MinimalEnStoryListViewModel>(() => data.value ?? []);

  const enRecommendedStories = computed<MinimalEnStoryListViewModel>(() => shuffle(enStories.value.filter((story) => story.id !== filter?.id)));

  return { enStories, enRecommendedStories, ...useAsyncDataRequestStatus(status) };
};
